// userUtils.ts
import { IUserRootResponse } from "../types/user";

// Feature types
export interface Feature {
  featureCode: string;
  featureValue: string;
}

export type FeatureCode =
  | "DURATION_PER_VIDEO"
  | "NUMBER_OF_VIDEOS_QUOTA"
  | "STORAGE_QUOTA"
  | "TEAMS_COUNT"
  | "TEAMS_STORAGE"
  | "TEAMS_CREATORS_COUNT"
  | "TEAMS_VIEWERS_COUNT"
  | "TRANSCRIPT"
  | "SUGGEST_METADATA"
  | "TRANSLATE"
  | "UPLOAD"
  | "SEARCH"
  | "RESOURCES"
  | "INTERACTION"
  | "CTA"
  | "SEE_ENGAGEMENT"
  | "DRAWING"
  | "MOUSE_ARROW"
  | "RECORDING_RESOLUTION"
  | "EXTENSION_INTEGRATION"
  | "CHAPTERS"
  | "APPEARANCE"
  | "TEAMS"
  | "SCHEDULE"
  | "STUDIO"
  | "INTEGRATION"
  | "PLAYLIST"
  | "AI"
  | string; // Allow for custom feature codes

/**
 * Extract user initials from user data
 * @param userData - User data from the store
 * @returns User initials as string (e.g., "JD" for John Doe)
 */
export const getUserInitials = (userData: IUserRootResponse | null): string => {
  if (!userData) {
    return "--";
  }

  const firstName =
    userData.firstName ||
    userData.first_name ||
    userData.givenName ||
    userData.given_name;
  const lastName =
    userData.lastName ||
    userData.last_name ||
    userData.surname ||
    userData.familyName ||
    userData.family_name;
  const fullName =
    userData.name ||
    userData.displayName ||
    userData.display_name ||
    userData.fullName ||
    userData.full_name;
  const email = userData.email;
  const username = userData.username || userData.userName || userData.user_name;

  // If we have both first and last name
  if (firstName && lastName) {
    return `${firstName.charAt(0).toUpperCase()}${lastName
      .charAt(0)
      .toUpperCase()}`;
  }

  // If we have a full name, try to split it
  if (fullName && typeof fullName === "string") {
    const nameParts = fullName.trim().split(/\s+/);
    if (nameParts.length >= 2) {
      return `${nameParts[0].charAt(0).toUpperCase()}${nameParts[
        nameParts.length - 1
      ]
        .charAt(0)
        .toUpperCase()}`;
    }
    if (nameParts.length === 1) {
      return nameParts[0].charAt(0).toUpperCase();
    }
  }

  // If we have only first name
  if (firstName) {
    return firstName.charAt(0).toUpperCase();
  }

  // If we have only last name
  if (lastName) {
    return lastName.charAt(0).toUpperCase();
  }

  // Try to extract from email (before @)
  if (email && typeof email === "string") {
    const emailName = email.split("@")[0];
    if (emailName.length >= 2) {
      return emailName.substring(0, 2).toUpperCase();
    }
    return emailName.charAt(0).toUpperCase();
  }

  // Try to extract from username
  if (username && typeof username === "string") {
    if (username.length >= 2) {
      return username.substring(0, 2).toUpperCase();
    }
    return username.charAt(0).toUpperCase();
  }

  // Final fallback - 2 letters when no name data is found
  return "--"; // Unknown - 2 letter fallback
};

/**
 * Get features array from user data
 * @param userData - User data from the store
 * @returns Array of features or empty array
 */
export const getFeatures = (userData: IUserRootResponse | null): Feature[] => {
  if (!userData || !userData.features || !Array.isArray(userData.features)) {
    return [];
  }
  return userData.features;
};

/**
 * Check if a feature is enabled (boolean features)
 * @param userData - User data from the store
 * @param featureCode - The feature code to check
 * @returns true if feature is enabled, false otherwise
 */
export const isFeatureEnabled = (
  userData: IUserRootResponse | null,
  featureCode: FeatureCode
): boolean => {
  const features = getFeatures(userData);
  const feature = features.find((f) => f.featureCode === featureCode);

  if (!feature) {
    return false;
  }

  return feature.featureValue.toLowerCase() === "true";
};

/**
 * Get feature value as a string
 * @param userData - User data from the store
 * @param featureCode - The feature code to get value for
 * @returns Feature value as string or null if not found
 */
export const getFeatureValue = (
  userData: IUserRootResponse | null,
  featureCode: FeatureCode
): string | null => {
  const features = getFeatures(userData);
  const feature = features.find((f) => f.featureCode === featureCode);

  return feature ? feature.featureValue : null;
};

/**
 * Get feature numeric limit (for quota/limit features)
 * @param userData - User data from the store
 * @param featureCode - The feature code to get limit for
 * @returns Numeric limit or null if not found/invalid
 */
export const getFeatureLimit = (
  userData: IUserRootResponse | null,
  featureCode: FeatureCode
): number | null => {
  const featureValue = getFeatureValue(userData, featureCode);

  if (!featureValue) {
    return null;
  }

  // Handle values wrapped in curly braces like "{999999999}"
  const cleanValue = featureValue.replace(/[{}]/g, "");
  const numericValue = parseInt(cleanValue, 10);

  return isNaN(numericValue) ? null : numericValue;
};

/**
 * Check if a feature has unlimited access (for quota/limit features)
 * @param userData - User data from the store
 * @param featureCode - The feature code to check
 * @returns true if feature has unlimited access, false otherwise
 */
export const isFeatureUnlimited = (
  userData: IUserRootResponse | null,
  featureCode: FeatureCode
): boolean => {
  const limit = getFeatureLimit(userData, featureCode);
  return limit === null || limit >= 999999999; // Consider very large numbers as unlimited
};

/**
 * Check if user has reached a feature limit
 * @param userData - User data from the store
 * @param featureCode - The feature code to check
 * @param currentUsage - Current usage count
 * @returns true if limit is reached, false otherwise
 */
export const isFeatureLimitReached = (
  userData: IUserRootResponse | null,
  featureCode: FeatureCode,
  currentUsage: number
): boolean => {
  const limit = getFeatureLimit(userData, featureCode);

  if (limit === null || isFeatureUnlimited(userData, featureCode)) {
    return false;
  }

  return currentUsage >= limit;
};

/**
 * Get remaining usage for a feature
 * @param userData - User data from the store
 * @param featureCode - The feature code to check
 * @param currentUsage - Current usage count
 * @returns Remaining usage or null if unlimited/not found
 */
export const getFeatureRemainingUsage = (
  userData: IUserRootResponse | null,
  featureCode: FeatureCode,
  currentUsage: number
): number | null => {
  const limit = getFeatureLimit(userData, featureCode);

  if (limit === null || isFeatureUnlimited(userData, featureCode)) {
    return null; // Unlimited
  }

  const remaining = limit - currentUsage;
  return Math.max(0, remaining);
};

// Boolean features that only need enabled check
const BOOLEAN_FEATURES: FeatureCode[] = [
  "UPLOAD",
  "SEARCH",
  "RESOURCES",
  "INTERACTION",
  "CTA",
  "SEE_ENGAGEMENT",
  "DRAWING",
  "MOUSE_ARROW",
  "CHAPTERS",
  "APPEARANCE",
  "TEAMS",
  "SCHEDULE",
  "STUDIO",
  "INTEGRATION",
  "PLAYLIST",
  "AI",
];

// Quota/limit features that need usage tracking
const QUOTA_FEATURES: FeatureCode[] = [
  "DURATION_PER_VIDEO",
  "NUMBER_OF_VIDEOS_QUOTA",
  "STORAGE_QUOTA",
  "TEAMS_COUNT",
  "TEAMS_STORAGE",
  "TEAMS_CREATORS_COUNT",
  "TEAMS_VIEWERS_COUNT",
  "TRANSCRIPT",
  "SUGGEST_METADATA",
  "TRANSLATE",
];

/**
 * Check if user has access to a specific feature (enabled and not at limit)
 * @param userData - User data from the store
 * @param featureCode - The feature code to check
 * @param currentUsage - Current usage count (optional, defaults to 0)
 * @returns true if user has access to the feature
 */
export const hasFeatureAccess = (
  userData: IUserRootResponse | null,
  featureCode: FeatureCode,
  currentUsage: number = 0
): boolean => {
  // For boolean features, just check if enabled
  if (BOOLEAN_FEATURES.includes(featureCode)) {
    return isFeatureEnabled(userData, featureCode);
  }

  // For quota/limit features, check if enabled and not at limit
  if (QUOTA_FEATURES.includes(featureCode)) {
    const isEnabled = isFeatureEnabled(userData, featureCode);
    const isNotAtLimit = !isFeatureLimitReached(
      userData,
      featureCode,
      currentUsage
    );
    return isEnabled && isNotAtLimit;
  }

  // For special features like RECORDING_RESOLUTION and EXTENSION_INTEGRATION
  // Just check if they have a value (not null/empty)
  const featureValue = getFeatureValue(userData, featureCode);
  return featureValue !== null && featureValue !== "";
};

/**
 * Get recording resolution limit
 * @param userData - User data from the store
 * @returns Recording resolution in pixels or null if not found
 */
export const getRecordingResolution = (
  userData: IUserRootResponse | null
): number | null => {
  return getFeatureLimit(userData, "RECORDING_RESOLUTION");
};

/**
 * Get available extension integrations
 * @param userData - User data from the store
 * @returns Array of available integrations or empty array
 */
export const getExtensionIntegrations = (
  userData: IUserRootResponse | null
): string[] => {
  const featureValue = getFeatureValue(userData, "EXTENSION_INTEGRATION");

  if (!featureValue) {
    return [];
  }

  // Handle values wrapped in curly braces like "{Github,Gmail}"
  const cleanValue = featureValue.replace(/[{}]/g, "");
  return cleanValue.split(",").map((integration) => integration.trim());
};

/**
 * Check if a specific extension integration is available
 * @param userData - User data from the store
 * @param integrationName - Name of the integration to check
 * @returns true if integration is available
 */
export const hasExtensionIntegration = (
  userData: IUserRootResponse | null,
  integrationName: string
): boolean => {
  const integrations = getExtensionIntegrations(userData);
  return integrations.includes(integrationName);
};

/**
 * Get all boolean features that are enabled
 * @param userData - User data from the store
 * @returns Array of enabled boolean feature codes
 */
export const getEnabledBooleanFeatures = (
  userData: IUserRootResponse | null
): FeatureCode[] => {
  return BOOLEAN_FEATURES.filter((featureCode) =>
    isFeatureEnabled(userData, featureCode)
  );
};

/**
 * Get all quota features with their limits
 * @param userData - User data from the store
 * @returns Object with feature codes as keys and limits as values
 */
export const getQuotaFeatures = (
  userData: IUserRootResponse | null
): Record<string, number | null> => {
  const quotas: Record<string, number | null> = {};

  QUOTA_FEATURES.forEach((featureCode) => {
    quotas[featureCode] = getFeatureLimit(userData, featureCode);
  });

  return quotas;
};
